// components/EmailOrderPdfModal.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  HStack,
  VStack,
  Select,
  Text,
  Box,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { useCognito } from "../utils/useCognito";

// Endpoint for emailing order PDF
const EMAIL_ORDER_ENDPOINT =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/submitOrderToDistributor";

// Admin endpoint: returns { pdfBase64, fileName, contentType }
const GET_DROPBOX_TEMP_LINK_ENDPOINT =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getDropboxTempLink";

// Simple distributor options (can be extended later)
const DISTRIBUTORS = [
  { label: "Altera", email: "orders@alteragrp.com" },
  { label: "Test", email: "ian@curagenesis.com" },
];

export default function EmailOrderPdfModal({
  isOpen,
  onClose,
  order,
  isTestMode = false,
}) {
  const toast = useToast();
  const { getIdToken } = useCognito();
  const [selectedEmail, setSelectedEmail] = useState(DISTRIBUTORS[0].email);
  const [sending, setSending] = useState(false);

  // Raw Dropbox "home" URL from the order
  const rawDropboxUrl = order?.orderPdfUrl || "";

  // State for iframe-friendly object URL built from base64
  const [iframeUrl, setIframeUrl] = useState("");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState("");

  // Decode base64 -> blob URL
  const buildPdfObjectUrl = (pdfBase64) => {
    const byteChars = atob(pdfBase64);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNums[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNums);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    return URL.createObjectURL(blob);
  };

  // Clean up object URL when it changes / unmounts
  useEffect(() => {
    return () => {
      if (iframeUrl) {
        URL.revokeObjectURL(iframeUrl);
      }
    };
  }, [iframeUrl]);

  const loadPdfPreview = useCallback(async () => {
    if (!isOpen) return;
    if (!order?.orderId || !rawDropboxUrl) {
      setIframeUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
      setPdfError("");
      setLoadingPdf(false);
      return;
    }

    setLoadingPdf(true);
    setPdfError("");

    // Revoke any previous URL safely via setter
    setIframeUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });

    try {
      const token = getIdToken();
      const res = await fetch(GET_DROPBOX_TEMP_LINK_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dropboxUrl: rawDropboxUrl,
          orderId: order.orderId,
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success || !json.pdfBase64) {
        throw new Error(
          json.message || `Failed to load PDF preview (HTTP ${res.status})`
        );
      }

      const url = buildPdfObjectUrl(json.pdfBase64);
      setIframeUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (err) {
      console.error("Failed to get Dropbox PDF preview:", err);
      setPdfError(err.message || "Failed to load PDF preview.");
      setIframeUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
    } finally {
      setLoadingPdf(false);
    }
  }, [isOpen, order?.orderId, rawDropboxUrl, getIdToken]); // 👈 no iframeUrl here

  // Load whenever modal opens for this order
  useEffect(() => {
    loadPdfPreview();
    // In test mode force selection to Test distributor
    if (isTestMode) {
      const testOpt = DISTRIBUTORS.find(d => /ian@curagenesis.com/i.test(d.email));
      if (testOpt) setSelectedEmail(testOpt.email);
    }
  }, [loadPdfPreview]);

  const handleSend = async () => {
    if (!order?.orderId) {
      toast({ title: "Missing order", status: "error" });
      return;
    }
    if (!rawDropboxUrl) {
      toast({
        title: "No PDF URL",
        description: "Regenerate the order PDF before emailing.",
        status: "error",
      });
      return;
    }

    setSending(true);
    try {
      const token = getIdToken();
      const res = await fetch(EMAIL_ORDER_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId: order.orderId,
          distributorEmail: selectedEmail,
          test: isTestMode,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || `Failed: HTTP ${res.status}`);
      }
      toast({
        title: "Email Sent",
        description: `Order ${order.orderId} emailed to ${selectedEmail}`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onClose?.();
    } catch (err) {
      console.error("Email send failed", err);
      toast({
        title: "Send Failed",
        description: err.message || "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Email Order to Distributor</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!order?.orderId ? (
            <Text color="red.500">No order selected.</Text>
          ) : (
            <HStack align="stretch" spacing={6} flexWrap="wrap">
              <Box
                flex={1}
                minW={["100%", "55%"]}
                maxH="70vh"
                overflow="auto"
                borderWidth="1px"
                borderRadius="md"
                p={2}
                bg="gray.50"
              >
                {!rawDropboxUrl ? (
                  <VStack
                    spacing={3}
                    align="center"
                    justify="center"
                    h="100%"
                  >
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      No PDF URL found for this order.
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Regenerate the order PDF first, then email.
                    </Text>
                  </VStack>
                ) : loadingPdf ? (
                  <VStack
                    spacing={3}
                    align="center"
                    justify="center"
                    h="100%"
                  >
                    <Spinner />
                    <Text fontSize="sm" color="gray.600">
                      Loading PDF preview…
                    </Text>
                  </VStack>
                ) : pdfError ? (
                  <VStack
                    spacing={3}
                    align="center"
                    justify="center"
                    h="100%"
                  >
                    <Text fontSize="sm" color="red.500" textAlign="center">
                      {pdfError}
                    </Text>
                    <Button size="sm" onClick={loadPdfPreview}>
                      Retry
                    </Button>
                  </VStack>
                ) : iframeUrl ? (
                  <>
                    <Box
                      as="iframe"
                      src={iframeUrl}
                      title="Order PDF"
                      width="100%"
                      height="100%"
                      style={{ minHeight: "68vh" }}
                    />
                    <Box mt={2} textAlign="right">
                      <Button
                        as="a"
                        href={iframeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                        variant="link"
                        colorScheme="teal"
                      >
                        Open in new tab
                      </Button>
                    </Box>
                  </>
                ) : (
                  <VStack
                    spacing={3}
                    align="center"
                    justify="center"
                    h="100%"
                  >
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      PDF preview not available.
                    </Text>
                  </VStack>
                )}
              </Box>

              <VStack
                flex={1}
                minW={["100%", "35%"]}
                spacing={4}
                align="stretch"
              >
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Order
                  </Text>
                  <Text fontSize="sm">ID: {order.orderId}</Text>
                  {order.patientName && (
                    <Text fontSize="sm">Patient: {order.patientName}</Text>
                  )}
                  {order.facilityName && (
                    <Text fontSize="sm">Facility: {order.facilityName}</Text>
                  )}
                </Box>

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Distributor
                  </Text>
                  <Select
                    value={selectedEmail}
                    onChange={(e) => setSelectedEmail(e.target.value)}
                  >
                    {DISTRIBUTORS.map((d) => {
                      const isTest = /ian@curagenesis.com/i.test(d.email);
                      const disabled = isTestMode && !isTest;
                      return (
                        <option key={d.email} value={d.email} disabled={disabled}>
                          {d.label} ({d.email}){disabled ? " - disabled in test mode" : ""}
                        </option>
                      );
                    })}
                  </Select>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Choose who receives this order PDF.
                  </Text>
                </Box>

                {isTestMode && (
                  <Box
                    bg="yellow.50"
                    borderLeft="4px solid"
                    borderColor="yellow.400"
                    p={3}
                    borderRadius="md"
                  >
                    <Text fontSize="sm" color="yellow.800">
                      Test Mode: backend will treat this as a test submission.
                    </Text>
                  </Box>
                )}

              </VStack>
            </HStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={sending}
          >
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSend}
            isLoading={sending}
            isDisabled={!order?.orderId || !rawDropboxUrl}
          >
            {sending ? "Sending" : "Send Email"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
